/**
 * Change-history marks for the read-only ProseMirror review (#252).
 * `HistoryRange`s are offsets into the review's plain text, which is exactly
 * the document's projection (`plainTextToZwDoc`), so they map 1:1. Styled by
 * the `zw-history-ins` / `zw-history-del` classes.
 */

import { Plugin, PluginKey, type EditorState } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import type { HistoryRange } from "../../lib/proseDiff";
import { textOffsetToDocPos } from "../../lib/zwdoc/plainText";
import { projectionOf } from "./projection";

const historyKey = new PluginKey<DecorationSet>("zwHistoryMarks");

function historyDecorations(
  state: EditorState,
  ranges: readonly HistoryRange[],
): DecorationSet {
  if (ranges.length === 0) return DecorationSet.empty;
  const projection = projectionOf(state);
  const decorations = ranges.flatMap((range) => {
    const from = textOffsetToDocPos(projection, range.from);
    const to = textOffsetToDocPos(projection, range.to);
    if (from >= to) return [];
    return [
      Decoration.inline(from, to, {
        class: range.type === "insert" ? "zw-history-ins" : "zw-history-del",
      }),
    ];
  });
  return DecorationSet.create(state.doc, decorations);
}

/** Marks for `ranges()`, recomputed on every doc replace (the review swaps docs). */
export function historyMarksPlugin(
  ranges: () => readonly HistoryRange[],
): Plugin<DecorationSet> {
  return new Plugin<DecorationSet>({
    key: historyKey,
    state: {
      init: (_config, state) => historyDecorations(state, ranges()),
      apply: (tr, value, _old, state) =>
        tr.docChanged || tr.getMeta(historyKey) === true
          ? historyDecorations(state, ranges())
          : value,
    },
    props: {
      decorations: (state) => historyKey.getState(state) ?? null,
    },
  });
}

export const refreshHistoryMarksMeta = [historyKey, true] as const;
